import React from 'react';
import { Button } from '../../ui';

interface SelectionBarProps {
  count: number;
  mode: 'manage' | 'select';
  /** Total results currently matching (for the "Select all N" affordance). */
  totalMatching?: number;
  onClear: () => void;
  onSelectAll?: () => void;
  selectingAll?: boolean;
  allMatchingSelected?: boolean;
  /** select-mode confirm. */
  onConfirm?: () => void;
  /** manage-mode bulk favourite (toggles by majority state). */
  onBulkFavourite?: () => void;
  /** manage-mode bulk delete. */
  onBulkDelete?: () => void;
  /** Whether a bulk op is in flight (disables actions). */
  busy?: boolean;
  selectedImageCount?: number;
  onAddToDataset?: () => void;
}

/**
 * The persistent selection / confirm bar — the surface that finally makes the
 * already-built cross-page `useSelection` count visible and actionable. Slides
 * up (`sheet-rise`) the moment a selection exists, brutalist: indigo left tick,
 * mono uppercase count, ghost links + action buttons. In `select` mode the
 * right cluster is a confirm button (`Add Selected (N)` — preserved verbatim);
 * in `manage` mode it carries bulk favourite / delete.
 */
const SelectionBar: React.FC<SelectionBarProps> = ({
  count,
  mode,
  totalMatching,
  onClear,
  onSelectAll,
  selectingAll = false,
  allMatchingSelected = false,
  onConfirm,
  onBulkFavourite,
  onBulkDelete,
  busy = false,
  selectedImageCount = 0,
  onAddToDataset,
}) => {
  if (count === 0 && (!onSelectAll || !totalMatching)) return null;

  const actionsBusy = busy || selectingAll;

  return (
    <div className="media-bulk-bar" role="region" aria-label="Selection actions">
      <span className="media-bulk-tick" aria-hidden="true" />
      <span className="media-bulk-count" aria-live="polite">
        {count} Selected
      </span>
      {typeof totalMatching === 'number' && totalMatching > count && (
        <span className="media-bulk-sub">of {totalMatching}</span>
      )}

      {onSelectAll && typeof totalMatching === 'number' && totalMatching > 0 && (
        <button
          type="button"
          className="media-bulk-link"
          onClick={onSelectAll}
          disabled={actionsBusy || allMatchingSelected}
        >
          {selectingAll
            ? 'Selecting all…'
            : allMatchingSelected
            ? `All ${totalMatching} matching selected`
            : `Select all ${totalMatching} matching`}
        </button>
      )}

      {count > 0 && (
        <button type="button" className="media-bulk-link" onClick={onClear} disabled={actionsBusy}>
          Clear
        </button>
      )}

      <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
        {mode === 'manage' ? (
          <>
            {onBulkFavourite && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onBulkFavourite}
                disabled={actionsBusy}
              >
                ★ Favourite
              </Button>
            )}
            {onAddToDataset && (
              <Button type="button" variant="primary" size="sm" onClick={onAddToDataset} disabled={actionsBusy || selectedImageCount === 0} title={selectedImageCount === 0 ? 'Select one or more images first.' : 'Add selected images to a new or existing dataset.'}>
                Add to dataset ({selectedImageCount})
              </Button>
            )}
            {onBulkDelete && (
              <Button
                type="button"
                variant="danger"
                size="sm"
                onClick={onBulkDelete}
                disabled={actionsBusy}
              >
                🗑 Delete
              </Button>
            )}
          </>
        ) : (
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={onConfirm}
            disabled={actionsBusy || count === 0}
          >
            Add Selected ({count})
          </Button>
        )}
      </div>
    </div>
  );
};

export default SelectionBar;
